import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { REQUEST } from '../../express.tokens';
import { Request } from 'express';
import { CookieService } from 'ngx-cookie-service';
import { getEnv } from '../lib/get-env';
import { getOrigin } from '../lib/get-origin';
import { Environment } from '../providers/config.service';
import { TransferStateService } from './transfer-state.service';

@Injectable({
  providedIn: 'root',
})
export class BrowserService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Optional() @Inject(REQUEST) private req: Request,
    private cookieService: CookieService,
    private transferState: TransferStateService
  ) {
    if (isPlatformBrowser(this.platformId) && this.getEnv() === 'local') {
      window['setFakeTenant'] = (tenant: string) => {
        if (!tenant) return window['removeFakeTenant']();
        this.cookieService.set('fakeTenant', tenant, 9627576230894, '/');
      };
      window['removeFakeTenant'] = () => this.cookieService.delete('fakeTenant', '/');
      window['setFakeExtensionMode'] = (mode: boolean) => {
        this.cookieService.set('fakeExtensionMode', mode ? 'true' : 'false', 9627576230894, '/');
      };
    }
  }

  /**
   * @returns https://picflow.com | https://dev.picflow.com | https://custom.picflow.com | http://localhost:4200
   */
  public getOrigin(): string {
    return this.transferState.withStateTransferInstant('PROTECTED_origin', (): string => {
      // If we get to this point (inside state transfer) and we are not running on the server
      // it means we are running without SSR. We are assuming that this only happens when running locally
      if (isPlatformBrowser(this.platformId)) return window.location.origin;
      return getOrigin(this.req);
    });
  }

  public getEnv(): Environment {
    return getEnv(this.getOrigin());
  }

  public isExtension(): boolean {
    if (this.getCookies()?.fakeExtensionMode === 'true') return true;
    const origin = this.getOrigin();
    if (origin.startsWith('chrome-extension://')) return true;
    return false;
  }

  /**
   * Either sub domain or custom domain
   */
  public isTenantDomain(): boolean {
    return !!this.getTenantCustomDomain() || !!this.getTenantSubDomain();
  }

  public getTenantCustomDomain(): string {
    return this.transferState.withStateTransferInstant('PROTECTED_tenant-custom-domain', (): string => {
      if (isPlatformBrowser(this.platformId)) return;
      const host = <string>this.req.headers['custom-domain-host'];
      if (!host) return undefined;
      return host.split('www.').join('');
    });
  }

  public getTenantSubDomain(): string {
    return this.transferState.withStateTransferInstant('PROTECTED_tenant-sub-domain', (): string => {
      // If we get to this point (inside state transfer) and we are not running on the server
      // it means we are running without SSR. We are assuming that this only happens when running locally
      const tenant = this.getCookies().fakeTenant;
      if (tenant) return tenant;
      if (isPlatformBrowser(this.platformId)) return;
      const origin = this.getOrigin();
      const env = this.getEnv();
      if (env === 'local') return;
      if (!origin.endsWith('picflow.com')) return; // We only want picflow subdomains
      const subDomain = origin
        .split('.')
        .filter(a => a !== 'www')[0]
        .split('://')[1];
      if (!subDomain) return;
      if (['dev', 'test', 'picflow'].includes(subDomain)) return;
      return subDomain;
    });
  }

  public getTenantPath(): string {
    if (this.getTenantSubDomain()) return this.getTenantSubDomain();
    // if (this.getTenantCustomDomain()) return 'CUSTOM';
  }

  public getCookies(): { [key: string]: string } {
    if (isPlatformBrowser(this.platformId)) return this.cookieService.getAll();
    const raw = this.req.get('Cookie') || '';
    const array = raw.split('; ');
    const obj: { [key: string]: string } = {};
    array.forEach(v => {
      const vals = v.split('=');
      obj[vals[0]] = vals[1];
    });
    return obj;
  }

  public urlIsAssetUrl(url: string) {
    return url.includes('/img_') || url.includes('/asset_');
  }

  public urlIsGalleryUrl(url: string) {
    const isAssetUrl = this.urlIsAssetUrl(url);
    if (isAssetUrl) return false;
    if (this.getTenantSubDomain()) return true;
    if (this.getTenantCustomDomain()) return true;
    if (url.includes('/gallery/')) return true;
    return false;
  }

  /**
   * @returns https://picflow.com | https://dev.picflow.com | http://localhost:4200
   */
  public getCorrectPicflowUrl() {
    const env = this.getEnv();
    if (env === 'local') return this.getOrigin();
    if (env === 'dev') return 'https://dev.picflow.com';
    if (env === 'test') return 'https://test.picflow.com';
    if (env === 'production') return 'https://picflow.com';
  }
}
